import { useState } from 'react';
import { Card, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CHARGE_PAGE_SIZE,
  getDashboard,
  getFundUsage,
  type PAAccount,
  type PAFundUsage,
  type PACharge,
} from '../api/money';
import { NO_VALUE, currentPeriod, formatInstantDay } from '../format';
import { Money } from '../money/Money';
import { DataTable, FactList, type Column } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { MonthSpinner } from '../money/MonthSpinner';
import { Pager } from '../money/Pager';
import { usePAQuery } from '../money/usePAQuery';
import { chargeTypeLabel } from '../money/labels';

/**
 * «Расход средств» — Appendix L §9: what the money went on, for ONE billing month.
 *
 * ⚠ «Общая сумма списания» IS `total_charged` FROM THE SERVER, and it is the total over the
 * WHOLE month rather than over the rows on screen. Summing the visible rows here would look
 * identical on a five-charge month and diverge silently on a large one — a total that shrank
 * as the reader paged would be worse than no total at all — so the figure is read, never
 * computed. `src/money-arithmetic.test.ts` is what keeps that true.
 *
 * §9 draws the result as TWO tables (a tariff block and a service block). This build renders
 * ONE table carrying the engine's own `charge_type` as a column, because the engine has more
 * classes than the spec's two boxes (`subscription_fee`, `usage`, `service`, and whatever a
 * later wave adds) and a two-box layout has to decide where an unrecognised one goes — which
 * is how a charge disappears from a screen whose whole job is to account for every rouble.
 * The type is shown and sortable by eye; nothing is hidden.
 *
 * # The account filter needs a second read, and that is stated rather than hidden
 *
 * The charge rows carry `account_id` and no login, and there is no lightweight «list this
 * contract's accounts» route on the portal surface — the logins live on the dashboard read.
 * So the filter is populated from `GET /pa/money/dashboard`, in its OWN query keyed on the
 * contract alone, so that changing the month does not re-read it. If that read fails the
 * filter is simply absent and the table still works: a broken chooser must not take the data
 * with it.
 */
export function FundUsageScreen() {
  return (
    <MoneyPage titleKey="usage.title" leadKey="usage.lead">
      {(contract) => <FundUsageBody contractID={contract.id} currency={contract.currency} />}
    </MoneyPage>
  );
}

function FundUsageBody({ contractID, currency }: { contractID: number; currency: string }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(() => currentPeriod());
  const [accountID, setAccountID] = useState<number | null>(null);
  // ⚠ §9 IS PAGED AND THE PAGE IS THE SERVER'S. Under Model A the engine posts one charge
  // row per linked service PER DAY, so two accounts and seven services put ~496 rows in a
  // month and a third account crosses the 500 ceiling. Before `offset` existed those rows
  // were unreachable AND uncounted; `total_charged` is now the month's SQL sum, so the
  // total on screen stays right while the page moves.
  const [offset, setOffset] = useState(0);

  const accounts = usePAQuery<PAAccount[]>(
    async () => (await getDashboard(contractID)).accounts,
    [contractID],
  );
  const usage = usePAQuery<PAFundUsage>(
    () => getFundUsage(contractID, { period, accountID, offset }),
    [contractID, period, accountID, offset],
  );

  const accountRows = accounts.query.status === 'ready' ? accounts.query.data : [];
  const loginOf = (id: number): string =>
    accountRows.find((a) => a.account_id === id)?.login ?? String(id);

  const columns: ReadonlyArray<Column<PACharge>> = [
    {
      key: 'name',
      labelKey: 'usage.columnName',
      track: 'minmax(0, 2fr)',
      render: (c) => (
        <>
          {c.name || chargeTypeLabel(c.charge_type, t)}
          <span className="pa-cell-sub">{chargeTypeLabel(c.charge_type, t)}</span>
        </>
      ),
    },
    {
      key: 'account',
      labelKey: 'usage.columnAccount',
      render: (c) => <span className="mono">{loginOf(c.account_id)}</span>,
    },
    {
      key: 'coverage',
      labelKey: 'usage.columnCoverage',
      render: (c) =>
        c.coverage_from || c.coverage_to
          ? `${formatInstantDay(c.coverage_from)} — ${formatInstantDay(c.coverage_to)}`
          : formatInstantDay(c.calculated_at),
    },
    {
      key: 'quantity',
      labelKey: 'usage.columnQuantity',
      // VERBATIM as the engine holds it — a NUMERIC rendered as its own decimal string. Not
      // parsed into a number and re-formatted: a quantity is a measured value, and
      // re-rounding one on a display path is how a figure stops matching the invoice.
      render: (c) => c.quantity ?? NO_VALUE,
    },
    {
      key: 'amount',
      labelKey: 'usage.columnAmount',
      className: 'pa-td-right',
      render: (c) => <Money minor={c.amount} currency={currency} />,
    },
  ];

  return (
    <>
      <Card className="pa-block pa-filters">
        <MonthSpinner
          labelKey="usage.periodLabel"
          period={period}
          onChange={(next) => {
            setOffset(0);
            setPeriod(next);
          }}
        />
        {accountRows.length > 1 ? (
          <div className="pa-picker">
            <label className="pa-picker-label" htmlFor="pa-usage-account">
              {t('usage.accountLabel')}
            </label>
            <Select<number | 'all'>
              id="pa-usage-account"
              className="pa-picker-select"
              value={accountID ?? 'all'}
              onChange={(v) => {
                setOffset(0);
                setAccountID(v === 'all' ? null : v);
              }}
              options={[
                { value: 'all' as const, label: t('usage.allAccounts') },
                ...accountRows.map((a) => ({ value: a.account_id, label: a.login })),
              ]}
            />
          </div>
        ) : null}
      </Card>

      <QueryFrame query={usage.query} reload={usage.reload}>
        {(data) => (
          <>
            <Card className="pa-block">
              <FactList
                facts={[
                  {
                    key: 'total',
                    label: t('usage.totalCharged'),
                    value: <Money minor={data.total_charged} currency={currency} strong />,
                    hint: t('usage.totalChargedHint'),
                  },
                ]}
              />
            </Card>
            <DataTable
              columns={columns}
              rows={data.items}
              rowKey={(c) => c.id}
              emptyKey="usage.empty"
            />
            <Pager
              offset={offset}
              pageSize={CHARGE_PAGE_SIZE}
              shown={data.items.length}
              total={data.total}
              onOffset={setOffset}
            />
          </>
        )}
      </QueryFrame>
    </>
  );
}
